import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, Download, FileDown, Users, Briefcase, Award,
  PhoneCall, Layers, Building
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.jsx';
import { useLanguageStore, useAuthStore, openLeadModal, getLocalized, useAppearanceStore } from '../store/index.js'; 
import { useTranslation } from '../i18n/translations.js';
import TradePricingRequestModal from '../components/TradePricingRequestModal.jsx';
import ProjectSpecification from '../components/b2b/ProjectSpecification.jsx';
import OrderStatusTracker from '../components/b2b/OrderStatusTracker.jsx';
import { trackEvent } from '../lib/analytics.js';
import { SEO } from '../components/SEO.jsx';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * 1. ПУБЛИЧНАЯ СТРАНИЦА B2B-ПРОГРАММЫ (B2BPage).
 */
export const B2BPage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const { isAuthenticated, user, refreshUser } = useAuthStore();
  const navigate = useNavigate();
  const [catalogue, setCatalogue] = useState(null);
  const [pricingOpen, setPricingOpen] = useState(false);

  const siteImages = useAppearanceStore((s) => s.config?.images || {});
  const b2bTranslation = t.b2b || {};
  const b2bDash = t.b2bDashboard || {};
  const authTrans = t.auth || {};

  const pageDict = {
    pendingStatus: { en: '⏳ Pending Review', ru: '⏳ На рассмотрении', nl: '⏳ In behandeling', ro: '⏳ În așteptare' },
    volume: { en: 'Volume I', ru: 'Том I', nl: 'Deel I', ro: 'Volumul I' },
    edition: { en: 'Trade Edition', ru: 'Профессиональное издание', nl: 'Handelseditie', ro: 'Ediție Profesională' },
    collectionBook: { en: 'Collection Book', ru: 'Каталог коллекций', nl: 'Collectieboek', ro: 'Cartea Colecției' },
    benefitsEyebrow: { en: 'Benefits', ru: 'Привилегии', nl: 'Voordelen', ro: 'Beneficii' },
    advantagesTitle: { en: 'Trade Program Advantages', ru: 'Преимущества программы для бизнеса', nl: 'Voordelen van het handelsprogramma', ro: 'Avantajele programului comercial' },
    coverTitleTop: { en: 'Collection', ru: 'Каталог', nl: 'Collectie', ro: 'Catalog' },
    coverTitleBottom: { en: 'Book', ru: 'Коллекций', nl: 'Boek', ro: 'Colecție' }
  };

  useEffect(() => {
    fetch(`${API}/api/site-settings/catalogue?lang=${language}&t=${Date.now()}`)
      .then((r) => r.json())
      .then((d) => setCatalogue(d?.catalogue || null))
      .catch(() => {});
  }, [language]);

  useEffect(() => {
    if (!user?.is_b2b || user?.is_approved) return;
    const interval = setInterval(() => { refreshUser(); }, 30000);
    return () => clearInterval(interval);
  }, [user?.is_b2b, user?.is_approved, refreshUser]);

  const isB2BApproved = isAuthenticated && user?.is_b2b && user?.is_approved;

  const handleDownloadCenter = () => {
    if (isB2BApproved) {
      navigate(`/${language}/b2b/downloads`);
      return;
    }
    if (!isAuthenticated) {
      toast.info(authTrans.accessDenied || 'Authentication required', {
        description: b2bDash.accessDeniedText || 'Please sign in to access the Trade Library.',
      });
      navigate(`/${language}/login`, { state: { redirectTo: `/${language}/b2b/downloads` } });
      return;
    }
    toast.info(authTrans.pendingApprovalTitle || 'Approval pending', {
      description: user?.is_b2b
        ? (b2bDash.pendingText || 'Your B2B account is still under review. We will email you once approved.')
        : (authTrans.accessDeniedText || 'Trade Library is reserved for verified B2B partners.'),
    });
  };

  const handleTradePricing = () => {
    if (!isAuthenticated) {
      toast.info(authTrans.accessDenied || 'Sign in required', {
        description: authTrans.loginSubtitle || 'Custom project pricing is reserved for verified B2B partners.',
      });
      navigate(`/${language}/register`);
      return;
    }
    if (!isB2BApproved) {
      toast.info(authTrans.pendingApprovalTitle || 'Approval pending', {
        description: user?.is_b2b
          ? (b2bDash.pendingText || 'Once approved you can request custom pricing.')
          : (authTrans.accessDeniedText || 'Trade pricing is available to verified B2B partners.'),
      });
      return;
    }
    setPricingOpen(true);
  };

  const handleDedicatedSupport = () => {
    window.dispatchEvent(new CustomEvent('openChatWidget', {
      detail: { seed: 'designer_welcome', source: 'b2b_advantages' },
    }));
  };

  const handleProjectCollaboration = () => {
    if (isAuthenticated) {
      navigate(`/${language}/custom-furniture`);
    } else {
      if (typeof openLeadModal === 'function') {
        openLeadModal('b2b_project_collab');
      }
    }
  };

  const finalB2bHeroImg = useMemo(() => {
    const cmsUrl = siteImages['b2b_hero']?.url || siteImages['b2b_hero'];
    let img = cmsUrl ? cmsUrl : 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1920&q=80';
    if (img && typeof img === 'string' && img.includes('/upload/')) {
      img = img.replace('/upload/', '/upload/w_1920,q_auto,f_auto/');
    }
    return img;
  }, [siteImages]);

  const finalB2bShowroomImg = useMemo(() => {
    const cmsUrl = siteImages['b2b_showroom']?.url || siteImages['b2b_showroom'];
    let img = cmsUrl ? cmsUrl : 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800&q=80';
    if (img && typeof img === 'string' && img.includes('/upload/')) {
      img = img.replace('/upload/', '/upload/w_1200,q_auto,f_auto/');
    }
    return img;
  }, [siteImages]);

  const benefits = [
    {
      icon: Download,
      title: b2bTranslation.downloadCenter || 'Download Center',
      description: b2bTranslation.advantage_1_desc || 'CAD files, BIM models, 3D assets and technical specifications.',
      onClick: handleDownloadCenter,
      testid: 'advantage-download-center',
      cta: isB2BApproved 
        ? (b2bTranslation.cta_open_library || 'Open Library →') 
        : (b2bTranslation.cta_signin_required || 'Sign in required'),
    },
    {
      icon: Award,
      title: b2bTranslation.tradePricing || 'Trade Pricing Matrix',
      description: b2bTranslation.advantage_2_desc || 'Pricing is tailored per project specification. Submit your project brief for custom calculation.',
      onClick: handleTradePricing,
      testid: 'advantage-trade-pricing',
      cta: b2bTranslation.cta_request_quote || 'Request Quote →',
    },
    {
      icon: Users,
      title: b2bTranslation.projectSupport || 'Dedicated Studio Support',
      description: b2bTranslation.advantage_3_desc || 'Direct line to our atelier concierge. Technical blueprints, materials, and custom lead-times.',
      onClick: handleDedicatedSupport,
      testid: 'advantage-dedicated-support',
      cta: b2bTranslation.cta_talk_concierge || 'Talk to Concierge →',
    },
    {
      icon: Briefcase,
      title: b2bTranslation.advantage_4_title || 'Project Collaboration',
      description: b2bTranslation.advantage_4_desc || 'Co-design custom furniture blueprints with our architectural studio.',
      onClick: handleProjectCollaboration,
      testid: 'advantage-project-collab',
      cta: b2bTranslation.cta_submit_brief || 'Submit Brief →',
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="b2b-page">
      <SEO title={b2bTranslation.title || "Trade & B2B Program"} path="b2b" />
      <TradePricingRequestModal open={pricingOpen} onClose={() => setPricingOpen(false)} />
      
      <section className="relative h-[70vh] overflow-hidden">
        <img src={finalB2bHeroImg} alt="B2B" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 flex items-center">
          <div className="container-custom w-full">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl lg:max-w-5xl text-white text-center mx-auto">
              <p className="text-caption text-white/60 mb-4 font-medium tracking-[0.2em] uppercase text-xs">{b2bTranslation.title || 'Trade Program'}</p>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extralight tracking-tight leading-[1.15] mb-6">
                {b2bTranslation.subtitle || 'Premium Interior Solutions'}
              </h1>
              
              <p className="text-body-lg text-white/80 max-w-xl mx-auto font-light leading-relaxed mb-6">
                {b2bTranslation.readySub || 'Join our trade program and access exclusive benefits designed for architects, interior designers, developers, and dealers.'}
              </p>
              
              {(() => {
                if (!isAuthenticated) {
                  return (
                    <Link to={`/${language}/register`}>
                      <Button className="btn-primary bg-white text-black rounded-none h-11 px-6 text-xs font-semibold tracking-wider uppercase animate-in fade-in duration-300" data-testid="b2b-register-btn">
                        {b2bTranslation.register || 'Register for Trade'}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  );
                }
                if (user?.is_b2b && user?.is_approved) {
                  return (
                    <Link to={`/${language}/b2b/dashboard`}>
                      <Button className="btn-primary bg-white text-black rounded-none h-11 px-6 text-xs font-semibold tracking-wider uppercase animate-in fade-in duration-300" data-testid="b2b-dashboard-btn">
                        {b2bDash.title || 'Go to Dashboard'}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  );
                }
                if (user?.is_b2b && !user?.is_approved) {
                  return (
                    <div className="p-6 bg-white/10 backdrop-blur-sm border-l-2 border-white max-w-lg mx-auto rounded-none text-left animate-in fade-in duration-300" data-testid="b2b-pending-banner">
                      <p className="text-caption text-white/60 mb-2">{pageDict.pendingStatus[language] || '⏳ Pending Review'}</p>
                      <h3 className="text-h3 text-white mb-2">{authTrans.pendingApprovalTitle}</h3>
                      <p className="text-white/80 text-sm">{authTrans.pendingApprovalText}</p>
                    </div>
                  );
                }
                return (
                  <Link to={`/${language}/register`}>
                    <Button className="btn-primary bg-white text-black rounded-none h-11 px-6 text-xs font-semibold tracking-wider uppercase animate-in fade-in duration-300" data-testid="b2b-upgrade-btn">
                      {authTrans.b2bRegister || 'Upgrade to Trade Account'}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                );
              })()}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits grid section */}
      <section className="section-padding bg-surface">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-caption text-muted-foreground mb-4">{b2bTranslation.eyebrow || pageDict.benefitsEyebrow[language]}</p>
            <h2 className="text-h1">{b2bTranslation.eligibilityTitle || pageDict.advantagesTitle[language]}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.button key={benefit.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} whileHover={{ y: -4 }} transition={{ delay: index * 0.08 }} onClick={benefit.onClick} className="text-left p-8 border border-border bg-background hover:border-foreground transition-colors flex flex-col group rounded-none" data-testid={benefit.testid}>
                <benefit.icon className="w-8 h-8 mb-6" strokeWidth={1.5} />
                <h3 className="text-lg font-medium mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground text-sm flex-1">{benefit.description}</p>
                <p className="mt-6 text-[10px] tracking-[0.25em] uppercase text-muted-foreground group-hover:text-foreground transition-colors">{benefit.cta}</p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Target audience eligibility section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-caption text-muted-foreground mb-4">{b2bTranslation.eligibilityEyebrow || 'Eligibility'}</p>
              <h2 className="text-h1 mb-8">{b2bTranslation.eligibilityTitle || 'Who Can Apply?'}</h2>
              <div className="space-y-6">
                {[
                  { title: b2bTranslation.architects || 'Architects', desc: b2bTranslation.architectsDesc || 'Licensed architecture practices and spatial planners' },
                  { title: b2bTranslation.designers || 'Interior Designers', desc: b2bTranslation.designersDesc || 'Professional interior design studios and consultants' },
                  { title: b2bTranslation.developers || 'Developers', desc: b2bTranslation.developersDesc || 'Premium property development and contracting companies' },
                  { title: b2bTranslation.dealers || 'Dealers', desc: b2bTranslation.dealersDesc || 'Authorized gallery and retail partners' },
                ].map((item, index) => (
                  <div key={item.title} className="flex gap-4" data-testid={`eligibility-${index}`}>
                    <div className="w-8 h-8 border border-foreground flex items-center justify-center text-sm shrink-0">0{index + 1}</div>
                    <div>
                      <h4 className="font-medium">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src={finalB2bShowroomImg} alt="Showroom" className="w-full aspect-[4/5] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Curated collection catalogue book */}
      <section className="section-padding bg-surface" data-testid="collection-book-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-caption text-muted-foreground mb-4">{b2bTranslation.catalogEyebrow || 'Trade Resources'}</p>
              <h2 className="text-h1 mb-6 font-extralight">
                {catalogue && getLocalized(catalogue.title, language) ? getLocalized(catalogue.title, language) : (pageDict.collectionBook[language] || 'Collection Book')}
              </h2>
              {catalogue && getLocalized(catalogue.subtitle, language) && (
                <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground -mt-4 mb-4">{getLocalized(catalogue.subtitle, language)}</p>
              )}
              <p className="text-body-lg max-w-xl font-light mb-8 leading-relaxed">
                {catalogue && getLocalized(catalogue.description, language) ? getLocalized(catalogue.description, language) : (b2bTranslation.catalogDescFallback || 'Our complete annual portfolio — every collection, every system, every finish. Bound in a single, beautifully crafted PDF.')}
              </p>
              <div className="space-y-3 mb-10 text-sm text-muted-foreground font-light">
                <p className="flex items-center gap-3"><span className="w-1 h-1 bg-foreground rounded-full"></span>{b2bTranslation.catalogBullet1 || 'Full catalogue · 180+ pages'}</p>
                <p className="flex items-center gap-3"><span className="w-1 h-1 bg-foreground rounded-full"></span>{b2bTranslation.catalogBullet2 || 'Technical drawings & material library'}</p>
                <p className="flex items-center gap-3"><span className="w-1 h-1 bg-foreground rounded-full"></span>{b2bTranslation.catalogBullet3 || 'Reserved for approved trade accounts'}</p>
              </div>
              {catalogue?.secure_url ? (
                <a href={catalogue.secure_url} target="_blank" rel="noopener noreferrer" download={catalogue.filename || 'Collection-Book.pdf'} onClick={() => trackEvent('catalogue_downloaded', { type: 'collection_book', source: 'b2b_landing', title: getLocalized(catalogue.title, language) || 'Collection Book' })} data-testid="collection-book-download-link">
                  <Button className="btn-primary rounded-none">
                    <FileDown className="mr-2 w-4 h-4" strokeWidth={1.5} />
                    {t.b2bDownloads?.downloadCollectionBook || 'Download Catalogue PDF'}
                  </Button>
                </a>
              ) : (
                <Button className="btn-primary rounded-none" disabled data-testid="collection-book-download-btn">
                  <FileDown className="mr-2 w-4 h-4" strokeWidth={1.5} />
                  {t.b2bDownloads?.availableSoon || 'Download PDF — Available soon'}
                </Button>
              )}
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[3/4] bg-background border border-border relative overflow-hidden group">
                {catalogue?.cover_image_url ? (
                  <img src={catalogue.cover_image_url} alt={getLocalized(catalogue.title, language) || 'Collection Book'} className="absolute inset-0 w-full h-full object-cover" data-testid="collection-book-cover-img" />
                ) : (
                  <>
                    <div className="absolute inset-0 flex flex-col justify-between p-8">
                      <div><p className="text-add-space text-[10px] tracking-[0.4em] uppercase text-muted-foreground">{t.newsletter?.title || 'FEDYK Furniture'}</p></div>
                      <div className="text-center">
                        <p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground mb-3">{pageDict.volume[language] || 'Volume I'}</p>
                        <h3 className="text-4xl md:text-5xl font-extralight tracking-tight leading-[1]">{pageDict.coverTitleTop[language] || 'Collection'}<br/>{pageDict.coverTitleBottom[language] || 'Book'}</h3>
                        <p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground mt-4">MMXXVI</p>
                      </div>
                      <div className="text-right"><p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground">{pageDict.edition[language] || 'Trade Edition'}</p></div>
                    </div>
                    <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #000 1px, transparent 1px), radial-gradient(circle at 80% 70%, #000 1px, transparent 1px)', backgroundSize: '8px 8px' }}></div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to action footer section */}
      <section className="section-padding bg-foreground text-background">
        <div className="container-custom text-center">
          <h2 className="text-h1 mb-6">{b2bTranslation.readyTitle || 'Ready to Get Started?'}</h2>
          <p className="text-body-lg text-background/70 mb-8 max-w-xl mx-auto">{b2bTranslation.readySub || 'Apply for our trade program today and unlock exclusive benefits for your projects.'}</p>
          <Link to={`/${language}/register`}>
            <Button className="btn-primary bg-background text-foreground rounded-none" data-testid="b2b-apply-btn">
              {b2bTranslation.readyBtn || 'Apply Now'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

/**
 * 2. СТРАНИЦА ЛИЧНОГО КАБИНЕТА B2B-ПАРТНЕРА (B2BDashboardPage).
 */
export const B2BDashboardPage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const { user, token } = useAuthStore();
  const navigate = useNavigate();
  
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  const l = t.b2bDashboard || {};

  const dashDict = {
    hubTitle: { en: 'Trade Partner Hub', ru: 'Личный кабинет B2B-партнера', nl: 'Partner Hub', ro: 'Portal partener' },
    consultBtn: { en: 'Project Studio Support', ru: 'Поддержка проектов', nl: 'Consult aanvragen', ro: 'Suport proiecte' },
    quoteBtn: { en: 'Request Calculation', ru: 'Трейд-калькуляция', nl: 'Offerte aanvragen', ro: 'Solicită ofertă' },
    assetDefault: { en: 'Asset', ru: 'Файл', nl: 'Bron', ro: 'Resursă' }
  };

  const assetCategories = {
    catalogue: t.b2bDownloads?.categoryCatalogue || 'Catalogues',
    '3d_model': t.b2bDownloads?.category3dModel || '3D Models',
    tech_pack: t.b2bDownloads?.categoryTechPack || 'Technical Packs',
    brochure: t.b2bDownloads?.categoryBrochure || 'Brochures',
    specification: t.b2bDownloads?.categorySpecification || 'Specifications',
    other: t.b2bDownloads?.categoryOther || 'Other'
  };

  const [formData, setFormData] = useState({
    first_name: '', last_name: '', phone: '', company_name: '', vat_number: '',
    company_street: '', company_office: '', company_city: '', company_postcode: '', company_country: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  const messages = {
    profileUpdated: { en: 'Profile updated successfully', ru: 'Профиль успешно обновлен', nl: 'Profiel succesvol bijgewerkt', ro: 'Profilul a fost actualizat cu succes' },
    sessionExpired: { en: 'Session expired. Please log in again.', ru: 'Сессия истекла. Пожалуйста, войдите снова.', nl: 'Sessie verlopen. Log opnieuw in.', ro: 'Sesiunea a expirat. Vă rugăm să vă autentificați din nou.' },
    updateFailed: { en: 'Failed to update profile', ru: 'Не удалось обновить профиль', nl: 'Profiel bijwerken mislukt', ro: 'Actualizarea profilului a eșuat' },
    serverError: { en: 'Server connection error. Please try again.', ru: 'Ошибка соединения с сервером. Пожалуйста, попробуйте еще раз.', nl: 'Verbindingsfout met server. Probeer het opnieuw.', ro: 'Eroare de conexiune la server. Vă rugăm să încercați din nou.' }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '', last_name: user.last_name || '', phone: user.phone || '',
        company_name: user.company_name || '', vat_number: user.vat_number || '',
        company_street: user.company_street || '', company_office: user.company_office || '',
        company_city: user.company_city || '', company_postcode: user.company_postcode || '', company_country: user.company_country || ''
      });
    }
  }, [user]);

  const getAuthToken = () => {
    try {
      const authData = localStorage.getItem('fedyk-auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed?.state?.token || parsed?.state?.access_token || null;
      }
    } catch (e) {
      console.error("Error reading auth token", e);
    }
    return null;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const token = getAuthToken();
      if (!token) throw new Error(messages.sessionExpired[language] || 'Session expired. Please log in again.');
      const res = await fetch(`${API}/api/auth/update-profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || messages.updateFailed[language] || 'Failed to update profile');
      toast.success(messages.profileUpdated[language] || 'Profile updated successfully');
    } catch (error) {
      toast.error(error.message || messages.serverError[language] || 'Server connection error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const fetchDownloads = async () => {
      try {
        const res = await fetch(`${API}/api/b2b/assets`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          const arr = data.assets || (Array.isArray(data) ? data : []);
          setDownloads(arr);
        }
      } catch (error) {
        console.error('Failed to fetch downloads:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchDownloads();
  }, [token]);

  if (!user || !user.is_b2b || !user.is_approved) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-6 text-center" data-testid="b2b-access-denied">
        <Building className="w-12 h-12 text-muted-foreground mb-4 font-light" strokeWidth={1} />
        <h2 className="text-xl font-extralight tracking-tight mb-2">{l.accessDenied || 'B2B Partner Access Only'}</h2>
        <p className="text-sm font-light text-[#4A4A4A] leading-relaxed mb-8">{l.accessDeniedText || 'Please log in with a verified professional B2B account to access the trade hub.'}</p>
        <Link to={`/${language}/b2b`}>
          <Button variant="outline" className="rounded-none border-[#ECECEC] hover:bg-[#FAFAFA] text-xs uppercase tracking-wider font-light text-[#0A0A0A]">{t.b2bApiKeys?.backToDashboard || 'Go Back'}</Button>
        </Link>
      </div>
    );
  }

  const isApproved = user.is_approved === true;

  return (
    <>
      <SEO title={`${l.title || 'Trade Dashboard'} — FEDYK`} description="Exclusive portal for verified architects, interior designers, and trade professionals." />
      <div className="container mx-auto px-4 md:px-8 py-12 max-w-7xl" data-testid="b2b-dashboard">
        
        {/* Header container */}
        <div className="border-b border-border pb-8 mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground">{l.eyebrow || dashDict.hubTitle[language]}</p>
              <span className={`text-[10px] tracking-wider uppercase px-2.5 py-0.5 border ${isApproved ? 'border-foreground text-foreground bg-foreground/5' : 'border-amber-500/50 text-amber-600 bg-amber-500/5'}`}>
                {isApproved ? (l.statusApproved || 'Verified Professional') : (l.statusPending || 'Pending Verification')}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-extralight tracking-tight">{l.welcome || 'Welcome back'}, {user.first_name || user.company_name}</h1>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button variant="outline" className="rounded-none px-6 text-xs tracking-widest uppercase font-light bg-transparent text-foreground border-foreground hover:bg-foreground hover:text-background transition-colors h-11" onClick={() => { trackEvent('b2b_consultation_clicked'); if (typeof openLeadModal === 'function') openLeadModal('b2b_project_collab'); }}>
              <PhoneCall className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
              {dashDict.consultBtn[language] || 'Request Consultation'}
            </Button>
            <Button variant="outline" className="rounded-none px-6 text-xs tracking-widest uppercase font-light bg-transparent text-foreground border-foreground hover:bg-foreground hover:text-background transition-colors h-11" onClick={() => { setPricingModalOpen(true); trackEvent('b2b_pricing_modal_opened'); }}>
              <Layers className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
              {dashDict.quoteBtn[language] || 'Request Quote'}
            </Button>
          </div>
        </div>

        {/* Dashboard workspace layout */}
        <div className="bg-surface border-t border-[#ECECEC] py-16 min-h-[calc(100vh-240px)]">
          <div className="container-custom">
            <Tabs defaultValue="downloads" className="space-y-10">
              <TabsList className="bg-white border border-[#ECECEC] p-1 rounded-none inline-flex shadow-sm">
                <TabsTrigger value="downloads" className="rounded-none text-xs tracking-wider uppercase text-[#9E9E9E] data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-6 py-2.5 transition-all font-medium h-9">{t.b2b?.downloadCenter || 'Download Center'}</TabsTrigger>
                <TabsTrigger value="pricing" className="rounded-none text-xs tracking-wider uppercase text-[#9E9E9E] data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-6 py-2.5 transition-all font-medium h-9">{t.b2b?.tradePricing || 'Trade Pricing'}</TabsTrigger>
                <TabsTrigger value="projects" className="rounded-none text-xs tracking-wider uppercase text-[#9E9E9E] data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-6 py-2.5 transition-all font-medium h-9" data-testid="dashboard-tab-projects">{l.projectsTab || 'Project Specifications'}</TabsTrigger>
                <TabsTrigger value="orders" className="rounded-none text-xs tracking-wider uppercase text-[#9E9E9E] data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-6 py-2.5 transition-all font-medium h-9" data-testid="dashboard-tab-orders">{l.ordersTab || 'Orders Tracking'}</TabsTrigger>
                <TabsTrigger value="api" className="rounded-none text-xs tracking-wider uppercase text-[#9E9E9E] data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-6 py-2.5 transition-all font-medium h-9">{l.apiTab || 'API Integration'}</TabsTrigger>
              </TabsList>

              <TabsContent value="downloads" className="outline-none min-h-[400px]">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                  <h2 className="text-3xl font-extralight text-[#0A0A0A] tracking-tight">{l.techFilesTitle || 'Technical Library'}</h2>
                  <Link to={`/${language}/b2b/downloads`}>
                    <button type="button" className="inline-flex items-center gap-2 rounded-none border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[10px] tracking-[0.25em] uppercase py-2.5 px-5 h-10 transition-all font-medium" data-testid="dashboard-open-download-center">{l.openDownloadCenter || 'Open Download Center'} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                  </Link>
                </div>
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="animate-pulse p-6 border border-[#ECECEC] bg-white">
                        <div className="h-4 bg-[#ECECEC] w-3/4 mb-4" />
                        <div className="h-3 bg-[#ECECEC] w-1/2" />
                      </div>
                    ))}
                  </div>
                ) : downloads.length === 0 ? (
                  <p className="text-sm font-light text-[#4A4A4A]">{l.noDownloads || 'No architectural assets available yet.'}</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {downloads.slice(0, 6).map((item) => {
                      const title = getLocalized(item.title, language) || item.filename || dashDict.assetDefault[language];
                      const readableCategory = assetCategories[item.category] || (item.category || 'file').replace('_', ' ');
                      return (
                        <a key={item.id} href={item.file_url} target="_blank" rel="noopener noreferrer" download={item.filename || title} className="group p-6 bg-white border border-[#ECECEC] hover:border-[#0A0A0A] hover:bg-neutral-50 transition-all shadow-sm flex flex-col" data-testid={`download-item-${item.id}`}>
                          <h3 className="font-medium text-[#0A0A0A] text-base mb-2 leading-tight group-hover:underline">{title}</h3>
                          {item.sub_tag && <p className="text-[9px] tracking-[0.2em] uppercase text-[#9E9E9E] mb-4 font-medium">{item.sub_tag}</p>}
                          <div className="mt-auto flex items-center gap-2 border-t border-[#ECECEC] pt-4 text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A]">
                            <FileDown className="w-4 h-4 text-[#9E9E9E]" strokeWidth={1.5} />
                            {readableCategory}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="pricing" className="outline-none min-h-[400px]">
                <div className="text-left">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] mb-4 font-medium">{l.tradePricingTitle || 'Trade Pricing'}</p>
                  <h2 className="text-3xl font-extralight tracking-tight text-[#0A0A0A] mb-6">{l.tradePricingTitle || 'Trade Pricing'}</h2>
                  <p className="text-sm font-light text-[#4A4A4A] leading-relaxed max-w-xl mb-8">
                    {language === 'ru' ? 'Ваша профессиональная трейд-привилегия автоматически учитывается при формировании проектных спецификаций.' : l.tradePricingDesc || 'Your trade discount is automatically applied to all product specifications across our digital environment.'}
                  </p>
                  <Link to={`/${language}/products`}>
                    <button type="button" className="inline-flex items-center justify-center rounded-none bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white !text-white text-xs tracking-[0.25em] uppercase py-3 px-6 h-11 transition-all font-medium shadow-sm w-fit">{l.browseProducts || 'Browse Lookbook'}</button>
                  </Link>
                </div>
              </TabsContent>

              <TabsContent value="projects" className="outline-none min-h-[400px]"><ProjectSpecification /></TabsContent>
              <TabsContent value="orders" className="outline-none min-h-[400px]"><OrderStatusTracker /></TabsContent>

              <TabsContent value="api" className="outline-none min-h-[400px]">
                <div className="text-left">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] mb-4 font-medium">{l.apiTitle || 'API Integration'}</p>
                  <h2 className="text-3xl font-extralight tracking-tight text-[#0A0A0A] mb-6">{l.apiTitle || 'API Integration'}</h2>
                  <p className="text-sm font-light text-[#4A4A4A] mb-8 max-w-2xl leading-relaxed">{l.apiDesc || 'Generate secure read-only API tokens to integrate the complete FEDYK Furniture digital portfolio.'}</p>
                  <Link to={`/${language}/b2b/api-keys`}>
                    <button type="button" className="inline-flex items-center justify-center rounded-none bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white !text-white text-xs tracking-[0.25em] uppercase py-3 px-6 h-11 transition-all font-medium shadow-sm w-fit" data-testid="dashboard-manage-keys">{l.manageKeys || 'Manage API keys →'}</button>
                  </Link>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <TradePricingRequestModal open={pricingModalOpen} onClose={() => setPricingModalOpen(false)} />
    </>
  );
};

export default B2BPage;
